import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  PlusIcon,
  ViewColumnsIcon,
  TableCellsIcon,
  CloudArrowUpIcon,
} from "@heroicons/react/24/solid";

import LeadForm from "./LeadForm";
import CardView from "./CardView";
import TableView from "./TableView";
import axios from "axios";
import { AuthContext } from "../context/AuthContext";
import { API_URL } from "../../Constants";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";
import { useSearchParams } from "react-router-dom";

export default function Lead() {
  // const [viewMode, setViewMode] = useState("card");
  const [searchParams, setSearchParams] = useSearchParams();

// read from URL
const viewMode = searchParams.get("view") || "card";
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [showUploadSection, setShowUploadSection] = useState(false);
  const [file, setFile] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [leads, setLeads] = useState([]);
  const { user } = useContext(AuthContext);
  const [refreshTable, setRefreshTable] = useState(false);


  useEffect(() => {
    async function fetchUsers() {
      if (!user?.id || !user?.accountType) return;

      try {
        const response = await fetch(
          `${API_URL}/api/companyuser/getByUserId?userId=${user.id}&accountType=${user.accountType}`,
          {
            method: "GET",
            headers: {
              authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        setUsers(data);
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    }

    fetchUsers();
  }, [user]);

  useEffect(() => {
    if (user) {
      setSelectedUser(user.id);
    }
  }, [user]);

  const handleFileChange = (e) => {
    const uploadedFile = e.target.files[0];
    if (uploadedFile) {
      const fileExtension = uploadedFile.name.split(".").pop().toLowerCase();
      if (["xls", "xlsx", "csv"].includes(fileExtension)) {
        setFile(uploadedFile);
      } else {
        alert("Only Excel (.xlsx, .xls) and CSV (.csv) files are allowed.");
        setFile(null);
      }
    }
  };

  const handleUploadFile = async (event) => {
    event.preventDefault(); // Prevents form refresh
    if (!file) {
      alert("Please select a valid file before uploading.");
      return;
    }

    if (!selectedUser) {
      alert("Please select an assigned user.");
      return;
    }

    const assignedUser = users.find((user) => user.id === selectedUser);
    if (!assignedUser) {
      alert("Assigned user not found.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append(
      "companyId",
      user.accountType === "COMPANY" ? user?.id : user.companyId
    ); // Assuming companyId is available in user context
    formData.append("createdById", user.id);
    formData.append("createdByName", user.name);
    formData.append("assignedToId", assignedUser.id);
    formData.append(
      "assignedToName",
      `${assignedUser.fname} ${assignedUser.lname}`
    );

    try {
      const response = await axios.post(
        `${API_URL}/api/leads/import/sheet`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      toast.success(response.data.message);
      setShowUploadSection(false);
    } catch (error) {
      console.error("Upload error:", error);
      alert("Error uploading file. Please try again.");
    }
  };

  const downloadExcelTemplate = () => {
    const worksheet = XLSX.utils.json_to_sheet([
      {
        "Lead Name": "test",
        Email: "test@gmail.com",
        Phone: "1234567890",
        "Company Name": "XYZ",
        "Lead Date(2025-10-01)": "2025-10-01",
        Address: "Delhi",
        City: "Delhi",
        State: "Delhi",
        Source: "Unknown",
        Status: "New",
        Project: "",
        Comment: "",
        "Follow-up Notes": "",
        "Follow-up Date": "2025-10-05",
        "Follow-up Time": "15:30:00",
      },
    ]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Leads");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(blob, "lead_template.xlsx");
  };

  // const handleToggleView = (mode) => {
  //   setViewMode(mode);
  // };
  const handleToggleView = (mode) => {
  setSearchParams({ view: mode });
};
  const handleAddForm = () => {
    setShowLeadForm(true);
  };

  const handleCloseForm = () => {
    setShowLeadForm(false);
    setRefreshTable((prev) => !prev);
  };

  const fetchLeads = async () => {
    try {
      const url =
        user?.accountType === "COMPANY"
          ? `${API_URL}/api/leads/company/${user.id}`
          : `${API_URL}/api/leads/assigned/${user.id}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setLeads(response.data.leads || []);
    } catch (error) {
      console.error("Error fetching data", error);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, []);

  const handleKeyDown = useCallback((event) => {
    const isEscape = event.key === "Escape";
    const isAltC =
      event.altKey &&
      (event.key.toLowerCase() === "c" || event.key.toUpperCase() === "C");

    if (isEscape || isAltC) {
      setShowLeadForm(false);
    }
  }, []);

  useEffect(() => {
    if (showLeadForm) {
      document.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [showLeadForm, handleKeyDown]);

  return (
    <>
      

      <div className="bg-[#FAF8FF] dark:bg-gray-900 rounded-lg p-1 sm:p-3 shadow-lg text-sm cfg-font">
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@600;700&family=Inter:wght@400;500;600&display=swap'); .cfg-heading{font-family:'Sora',sans-serif;color:#1F2937;} .cfg-font{font-family:'Inter',sans-serif;}`}</style>
        <form className="bg-[#FAF8FF] dark:bg-gray-800 p-1 sm:p-2 rounded-lg">
          <div className="flex flex-wrap justify-between items-center">
            <div className="flex flex-wrap items-center justify-center sm:justify-start space-x-1">
              <button
                type="button"
                onClick={() => handleToggleView("card")}
                className={`p-1 rounded ${
                  viewMode === "card" ? "bg-[#673AB7] text-white" : "bg-[#F3F4F6]"
                }`}
              >
                <ViewColumnsIcon className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => handleToggleView("table")}
                className={`p-1 rounded ${
                  viewMode === "table"
                    ? "bg-[#673AB7] text-white"
                    : "bg-[#F3F4F6]"
                }`}
              >
                <TableCellsIcon className="w-4 h-4" />
              </button>
            </div>




            <div className="flex flex-wrap justify-center sm:justify-end gap-1 sm:gap-1">
              <button
                type="button"
                onClick={handleAddForm}
                className="p-1 bg-[#673AB7] text-white rounded-full hover:bg-[#5E35A6] transition"
              >
                <PlusIcon className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => setShowUploadSection(!showUploadSection)}
                className="p-1 bg-[#8B5CF6] text-white rounded-full"
              >
                <CloudArrowUpIcon className="w-4 h-4" />
              </button>
            </div>
          </div>

          {showUploadSection && (
            <div className="relative bg-white mt-1 p-3 rounded shadow mb-3 dark:bg-gray-900 dark:text-white text-xs">
              {/* Close button */}
              <button
                onClick={() => setShowUploadSection(false)}
                className="absolute top-1 right-1 text-gray-500 hover:text-gray-700 dark:text-gray-300"
                aria-label="Close upload section"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>

              {/* Upload Section Content */}
              <label className="block text-xs font-medium  abs
              
               
              mb-1">
                Assign User
              </label>
              <select
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                className="w-full p-1 border border-[#E5E7EB] rounded dark:bg-gray-900 dark:text-white text-xs focus:outline-none focus:ring-2 focus:ring-[#E9D5FF] focus:border-[#673AB7]"
              >
                <option value="all">Select All</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.fname} {user.lname}
                  </option>
                ))}
              </select>

              <label className="block text-xs font-medium mt-1">
                Upload File
              </label>
              <input
                type="file"
                onChange={handleFileChange}
                className="w-full p-1 border border-[#E5E7EB] rounded text-xs focus:outline-none focus:ring-2 focus:ring-[#E9D5FF] focus:border-[#673AB7]"
              />

              <div className="mt-1">
                <p className="text-xs text-gray-600">
                  To Preview the excel format Click{" "}
                  <button
                    onClick={downloadExcelTemplate}
                    className="text-[#673AB7] underline"
                  >
                    here
                  </button>
                  .
                </p>
              </div>

              <button
                onClick={handleUploadFile}
                className="mt-1 p-1 bg-[#673AB7] text-white rounded text-xs hover:bg-[#5E35A6]"
              >
                Upload
              </button>
            </div>
          )}
        </form>

        {showLeadForm && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-3 sm:px-4 lg:px-6 overflow-y-auto"
            role="dialog"
            aria-modal="true"
          >
            <div className="relative w-full max-w-3xl bg-white dark:bg-gray-800 rounded shadow-2xl my-8 max-h-[90vh] overflow-hidden text-sm">
              {/* Sticky header with title and close button */}
              <div className="sticky top-0 z-20 flex items-center justify-between bg-white dark:bg-gray-800 p-3 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold cfg-heading dark:text-white">
                  Create Lead
                </h2>
                <button
                  onClick={handleCloseForm}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-300"
                  aria-label="Close modal"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {/* Scrollable content */}
              <div className="overflow-y-auto p-4 sm:p-5 md:p-6 max-h-[75vh]">
                <LeadForm onClose={handleCloseForm} onSuccess={fetchLeads} />
              </div>
            </div>
          </div>
        )}
        {viewMode === "card" ? (
          <CardView refreshTrigger={refreshTable} />
        ) : (
          <TableView refreshTrigger={refreshTable} />
        )}
      </div>
    </>
  );
}
